import { Dispatch } from "react";
import axios, { AxiosResponse } from "axios";
import jwtAxios from "../../auth/jwtAxios";
import { fetchError } from "./Common";
import { getDisplayErrorMessage } from "../../utils/StringUtils";
import { AppActions } from "../../types";
import { RisOrg, RisOrgRequest } from "../../types/models/RisOrg";
import {
  RisAddUserToOrgRequest,
  RisOrgUser,
  RisUpdateUserRequest,
  RisUserSearchRequest,
  RisUserSearchResponse,
} from "../../types/models/RisOrgUser";

/**
 * Org and user management through RIS (api/ris). The API passes on RIS's reason for rejecting a
 * request (e.g. "This user belongs to another org") as { Status }, so show that when present.
 */
const getRisErrorMessage = (error: Error) => {
  if (axios.isAxiosError(error)) {
    const status = (error.response?.data as { Status?: unknown } | undefined)?.Status;
    if (typeof status === "string" && status.length > 0) {
      return status;
    }
  }
  return getDisplayErrorMessage(error);
};

/**
 * Runs a request, calling back with the body on one of the expected status codes, or showing an
 * error and calling back with undefined otherwise.
 */
const risRequest = <T>(
  request: () => Promise<AxiosResponse<T>>,
  expectedStatuses: number[],
  callBack: (result: T | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    request()
      .then((response) => {
        if (expectedStatuses.includes(response.status)) {
          callBack(response.data);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + response.status));
          callBack(undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getRisErrorMessage(error)));
        callBack(undefined);
      });
  };
};

export const getRisOrgs = (callBack: (orgs: RisOrg[] | undefined) => void) =>
  risRequest(() => jwtAxios.get<RisOrg[]>("api/ris/orgs"), [200], callBack);

export const createRisOrg = (request: RisOrgRequest, callBack: (org: RisOrg | undefined) => void) =>
  risRequest(() => jwtAxios.post<RisOrg>("api/ris/orgs", request), [201], callBack);

export const updateRisOrg = (
  orgId: string,
  request: RisOrgRequest,
  callBack: (org: RisOrg | undefined) => void
) => risRequest(() => jwtAxios.patch<RisOrg>(`api/ris/orgs/${orgId}`, request), [200], callBack);

export const searchRisUsers = (
  request: RisUserSearchRequest,
  callBack: (result: RisUserSearchResponse | undefined) => void
) =>
  risRequest(
    () => jwtAxios.get<RisUserSearchResponse>("api/ris/users", { params: request }),
    [200],
    callBack
  );

/** 200 when an existing user was added, 201 when a new pending user was created. */
export const addUserToRisOrg = (
  orgId: string,
  request: RisAddUserToOrgRequest,
  callBack: (user: RisOrgUser | undefined) => void
) =>
  risRequest(
    () => jwtAxios.post<RisOrgUser>(`api/ris/orgs/${orgId}/users`, request),
    [200, 201],
    callBack
  );

export const removeUserFromRisOrg = (
  orgId: string,
  userId: string,
  callBack: (user: RisOrgUser | undefined) => void
) =>
  risRequest(
    () => jwtAxios.delete<RisOrgUser>(`api/ris/orgs/${orgId}/users/${userId}`),
    [200],
    callBack
  );

export const updateRisUser = (
  userId: string,
  request: RisUpdateUserRequest,
  callBack: (user: RisOrgUser | undefined) => void
) =>
  risRequest(() => jwtAxios.patch<RisOrgUser>(`api/ris/users/${userId}`, request), [200], callBack);
